import { EquityDividendFormData } from '@/types/equityDividend.ts';
import {EquityDividendWithAccountsJoin} from "@/integrations/supabase/equity.models.ts";

/**
 * Builds the equity_dividends insert payload.
 * Pure function exported for property-based testing.
 *
 * @param userId - The authenticated user's ID.
 * @param formData - The dividend form data.
 * @param convertedAmount - The amount converted to the target account's currency.
 */
export function buildEquityDividendPayload(
  userId: string,
  formData: EquityDividendFormData,
  convertedAmount: number
): Omit<EquityDividendWithAccountsJoin, 'id' | 'created_at' | 'updated_at' | 'accounts'> {
  return {
    user_id: userId,
    equity_security_id: formData.equity_security_id,
    target_account_id: formData.target_account_id,
    amount: formData.amount,
    currency: formData.currency,
    converted_amount: convertedAmount,
    payment_date: formData.payment_date,
    notes: formData.notes ?? null,
  };
}

/**
 * Builds the transactions insert payload for the dividend account credit.
 * Pure function exported for property-based testing.
 *
 * @param userId - The authenticated user's ID.
 * @param formData - The dividend form data.
 * @param equitySymbol - The stock symbol (e.g. "AAPL") for the description.
 * @param categoryId - The resolved category ID for the transaction.
 * @param convertedAmount - The amount in the target account's currency.
 * @param targetAccountCurrency - The currency of the target account.
 */
export function buildEquityDividendTransactionPayload(
  userId: string,
  formData: EquityDividendFormData,
  equitySymbol: string,
  categoryId: string,
  convertedAmount: number,
  targetAccountCurrency: string
): object {
  return {
    user_id: userId,
    account_id: formData.target_account_id,
    type: 'income' as const,
    category_id: categoryId,
    amount: convertedAmount,
    currency: targetAccountCurrency,
    description: `Dividend from ${equitySymbol}`.slice(0, 255),
    transaction_date: formData.payment_date,
  };
}

/**
 * Computes total dividends from an array of EquityDividend records.
 * Returns 0 for an empty array.
 *
 * @param dividends - Array of EquityDividend records.
 */
export function computeTotalEquityDividends(dividends: EquityDividendWithAccountsJoin[]): number {
  return dividends.reduce((sum, d) => sum + d.amount, 0);
}

/**
 * Aggregates securities transactions by date and computes cumulative share count.
 * Returns sorted array of { date, cumulativeShares } data points.
 * Same-date transactions are summed before computing the running total.
 * Cumulative values are rounded to 4 decimal places.
 *
 * @param transactions - Array of transaction objects with transaction_date and amount.
 */
export function computeSharePurchaseTrend(
  transactions: Array<{ transaction_date: string; amount: number }>
): Array<{ date: string; cumulativeShares: number }> {
  const grouped = new Map<string, number>();
  for (const tx of transactions) {
    const dateKey = tx.transaction_date.split('T')[0];
    grouped.set(dateKey, (grouped.get(dateKey) ?? 0) + tx.amount);
  }

  let cumulative = 0;
  return Array.from(grouped.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, quantity]) => {
      cumulative += quantity;
      return { date, cumulativeShares: parseFloat(cumulative.toFixed(4)) };
    });
}

type EquityProfitLossResult = {
  profitLoss: number;
  profitLossPercentage: number;
  currentValue: number;
  costBasis: number;
}

export function calculateEquityProfitLoss(
    sharesOwned: number,
    purchasePrice: number,
    currentPrice: number
): EquityProfitLossResult {
  const costBasis = sharesOwned * purchasePrice;
  const currentValue = sharesOwned * currentPrice;
  const profitLoss = currentValue - costBasis;
  const profitLossPercentage = costBasis !== 0 ? (profitLoss / costBasis) * 100 : 0;

  return {
    profitLoss,
    profitLossPercentage,
    currentValue,
    costBasis,
  };
}
